import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    userId: String,
  };

  connect() {
    // OneSignal ne fonctionne qu'en HTTPS (production)
    if (window.location.protocol !== "https:") {
      return;
    }

    this.waitForOneSignal()
      .then(() => {
        this.initializeOneSignal();
      })
      .catch((error) => {
        console.error("Failed to initialize OneSignal:", error);
        this.showNotification(
          "Failed to initialize push notifications. Please refresh the page.",
          "error"
        );
      });
  }

  async waitForOneSignal() {
    return new Promise((resolve, reject) => {
      if (window.OneSignalReady && window.OneSignal) {
        resolve();
        return;
      }

      const onReady = () => {
        window.removeEventListener("onesignal-ready", onReady);
        clearTimeout(timeout);
        resolve();
      };

      window.addEventListener("onesignal-ready", onReady);

      const timeout = setTimeout(() => {
        window.removeEventListener("onesignal-ready", onReady);
        reject(new Error("OneSignal failed to load"));
      }, 15000);
    });
  }

  async initializeOneSignal() {
    try {
      await OneSignal.init({
        appId: "1e5d28f6-2d8a-41f7-b11e-d0f95907b9ec",
        safari_web_id:
          "web.onesignal.auto.1e5d28f6-2d8a-41f7-b11e-d0f95907b9ec",
        notifyButton: {
          enable: true,
        },
        allowLocalhostAsSecureOrigin: true,
      });

      if (this.userIdValue) {
        await this.setExternalUserId();
      }

      OneSignal.User.PushSubscription.addEventListener(
        "change",
        this.handleSubscriptionChange.bind(this)
      );

      this.checkSubscriptionStatus();
    } catch (error) {
      console.error("OneSignal initialization failed:", error);
      this.showNotification(
        "Error initializing push notifications. Please try again later.",
        "error"
      );
    }
  }

  async setExternalUserId() {
    try {
      await OneSignal.login(this.userIdValue);
    } catch (error) {
      console.error("Failed to set external user ID:", error);
    }
  }

  async handleSubscriptionChange(event) {
    const isSubscribed = event.current.optedIn;

    if (isSubscribed && event.current.id) {
      const playerId = event.current.id;
      await this.savePlayerIdToBackend(playerId);
    }
  }

  async checkSubscriptionStatus() {
    try {
      const isSubscribed = OneSignal.User.PushSubscription.optedIn;
      const token = OneSignal.User.PushSubscription.id;

      if (isSubscribed && token) {
        await this.savePlayerIdToBackend(token);
      }
    } catch (error) {
      console.error("Error checking subscription status:", error);
    }
  }

  async savePlayerIdToBackend(playerId) {
    try {
      const response = await fetch("/api/v1/user_devices", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          user_device: {
            onesignal_player_id: playerId,
            device_type: "web",
            device_name: navigator.userAgent,
            push_enabled: true,
          },
        }),
      });

      const data = await response.json();

      if (response.ok) {
        this.showNotification(
          data.status?.message || "Device registered for notifications",
          "success"
        );
      } else {
        console.error("Failed to save player ID to backend");
        this.showNotification(
          data.status?.message || "Failed to register device",
          "error"
        );
      }
    } catch (error) {
      console.error("Error saving player ID:", error);
      this.showNotification(
        "Error registering device for notifications",
        "error"
      );
    }
  }

  async requestNotificationPermission() {
    try {
      if (!window.OneSignalReady || !window.OneSignal) {
        await this.waitForOneSignal();
      }

      await OneSignal.Notifications.requestPermission();
      this.showNotification("Notification permission requested", "info");
    } catch (error) {
      console.error("Failed to show notification prompt:", error);
      this.showNotification(
        "Failed to request notification permission",
        "error"
      );
    }
  }

  async sendTestNotification() {
    try {
      const response = await fetch("/api/v1/notifications/test", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          message: "Test notification from GO4School",
          title: "Test Notification",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        this.showNotification(
          data.status?.message || "Test notification sent successfully",
          "success"
        );
      } else {
        console.error("Failed to send test notification");
        this.showNotification(
          data.status?.message || "Failed to send test notification",
          "error"
        );
      }
    } catch (error) {
      console.error("Error sending test notification:", error);
      this.showNotification("Error sending test notification", "error");
    }
  }

  showNotification(message, type = "info") {
    const event = new CustomEvent("notification", {
      detail: { message, type },
    });
    document.dispatchEvent(event);
  }

  getNotificationPermission() {
    try {
      if (OneSignal && OneSignal.Notifications) {
        return OneSignal.Notifications.permission;
      }
      if ("Notification" in window) {
        return Notification.permission;
      }
      return "unsupported";
    } catch (error) {
      console.error("Error getting notification permission:", error);
      return "error";
    }
  }

  async isSubscribed() {
    try {
      if (!window.OneSignalReady || !window.OneSignal) {
        await this.waitForOneSignal();
      }

      return OneSignal.User.PushSubscription.optedIn;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return false;
    }
  }

  async getPushToken() {
    try {
      if (!window.OneSignalReady || !window.OneSignal) {
        await this.waitForOneSignal();
      }

      return OneSignal.User.PushSubscription.token;
    } catch (error) {
      console.error("Error getting push token:", error);
      return null;
    }
  }

  disconnect() {
    try {
      if (window.OneSignal && OneSignal.User) {
        OneSignal.User.PushSubscription.removeEventListener(
          "change",
          this.handleSubscriptionChange.bind(this)
        );
      }
    } catch (error) {
      console.error("Error cleaning up OneSignal listeners:", error);
    }
  }
}
